import React, { FC, Fragment, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { SidebarMenu as SidebarMenuLanguage } from '../../../../../../i18n'
import { RxJSCurrentState, useRxJSState } from '../../../../../React/Common/Hooks/useRxJSState'
import { MainMenuApp, MainMenuAppTransfer } from '../../../../../NonReact/constants/AppMenuConstants'
import { appMenuUpdate$ } from '../../../../../NonReact/common/RxJS/AppMenuState'
import { RouteID } from '../Types/Types'
import { IconName } from '../../../../../React/Common/Components/Icon'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, MessageContext } from '../../../../../React/Common/Contexts'
import { redirectToServletWithBranding, ServletCmds } from '../../../../../NonReact/constants/ServletCommands'
import { Branding } from '../../../../../React/Common/API/BrandingAPI'
import { useBranding } from '../../../../../React/Common/Hooks/useBranding'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import UsersByExtensionAdapter from '../../../../../React/UsersByExtension/Adapter/UsersByExtensionAdapter'
import ManageQueuesAdapter from '../../../../../React/ManageQueues/Adapter/ManageQueuesAdapter'
import SelfProvisioningAdapter from '../../../../../React/SelfProvisioning/Adapter/SelfProvisioningAdapter'
import { useCurrentPage } from '../../../../Hooks/useCurrentPage'
import { Permissions } from '../../../../../React/Common/Contexts/UserInfoContext'
import { hasPermission } from '../../../../../React/Common/Utils/PermissionsUtil'
import { IsVarEmpty, isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { userPagePermissions$ } from '../../../../../NonReact/common/RxJS/UserPagePermissionsState'
import { UserInfo, useUserInfo } from '../../../../Hooks/useUserInfo'
import { reportingMenuTrigger$ } from '../../../../../NonReact/common/RxJS/ReportingMenuTriggerState'
import { subMenuItemsState$ } from '../../../../../NonReact/common/RxJS/SubMenuItemsState'
import { useRxJSMemo } from '../../../../../React/Common/Hooks/useRxJSMemo'
import { reportingMenuFavourites$ } from '../../../../../NonReact/common/RxJS/ReportingMenuFavouritesState'
import { UserSettings, useUserSettings } from '../../../../../React/Common/Hooks/useUserSettings'
import { MessageContextType } from '../../../../../React/Common/Contexts/MessageContext'
import { SideMenuEntry } from './SideMenuEntry'
import { moveToURL } from '../../../../../NonReact/constants/LocationUtils'
import {
    CLIENT_CONFIGURATION_ROUTE_PATH,
    CRM_INTEGRATION_ROUTE_PATH,
    INSIGHTS_ROUTE_PATH,
    PRESENCE_URL
} from '../../../../Constants/Constants'
import { useFeatureFlag } from '../../../../../React/Common/Hooks/useFeatureFlag'
import {
    ENABLE_CLIENT_CONFIGURATION,
    ENABLE_CRM_CONFIGURATION,
    ENABLE_NEW_INSIGHTS
} from '../../../../Constants/FeatureFlags'
import { useIsCXIEnabled } from '../../../../../React/Common/Hooks/useIsCXIEnabled'
import { SubMenuItem, UserPagePermissions } from '../../../../../NonReact/common/Types/Types'
import { SidebarMenuItems } from '../../../../../NonReact/constants/ReactRouter/Header/Constants'
import { updateUserFavourites } from '../../../../../NonReact/common/API/EntityAPIClient'
import { Page } from '../../../../../NonReact/constants/Components/Page'

type Props = {
    routeID: Definable<RouteID>
    isSidebarExpanded: boolean
}

export const SidebarMenu: FC<Props> = ({ routeID, isSidebarExpanded }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(SidebarMenuLanguage.NAME)

    const currentJSPPage: Nullable<Page> = useCurrentPage()

    const branding: Nullable<Branding> = useBranding()

    const clientConfigEnabled: Definable<boolean> = useFeatureFlag(ENABLE_CLIENT_CONFIGURATION)
    const crmConfigEnabled: Definable<boolean> = useFeatureFlag(ENABLE_CRM_CONFIGURATION)

    const isNewInsightsEnabled: Definable<boolean> = useFeatureFlag(ENABLE_NEW_INSIGHTS)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const { displayError }: MessageContextType = useContext(MessageContext)

    const [userInfo]: UserInfo = useUserInfo()
    const [mainMenuApp]: RxJSCurrentState<MainMenuAppTransfer> = useRxJSState<MainMenuAppTransfer>(appMenuUpdate$)
    const [userPagePermissions]: RxJSCurrentState<UserPagePermissions> =
        useRxJSState<UserPagePermissions>(userPagePermissions$)
    const [isReportingMenuOpen, setIsReportingMenuOpen]: RxJSCurrentState<boolean> =
        useRxJSState<boolean>(reportingMenuTrigger$)
    const [favourites, setFavourites]: RxJSCurrentState<SidebarMenuItems[]> = useRxJSState<SidebarMenuItems[]>(
        reportingMenuFavourites$,
        []
    )
    const [userSettings]: UserSettings = useUserSettings()
    const isCXIEnabled: boolean = useIsCXIEnabled()

    const brandingURLMapping: string = useMemo((): string => branding?.URLMapping ?? EMPTY_STRING, [branding])

    const subMainMenuItems: Record<MainMenuApp, SubMenuItem[]> = useRxJSMemo(
        subMenuItemsState$,
        (): Record<MainMenuApp, SubMenuItem[]> =>
            IsVarEmpty(userInfo) || IsVarEmpty(userPagePermissions)
                ? {
                      [MainMenuApp.REPORTING]: [],
                      [MainMenuApp.FAST_PROVISIONING]: [],
                      [MainMenuApp.ADMIN]: [],
                      [MainMenuApp.STATUS]: []
                  }
                : {
                      [MainMenuApp.REPORTING]: [
                          {
                              id: SidebarMenuItems.REPORTING,
                              icon: IconName.REPORTING_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Reporting_Reporting),
                              enabled: userPagePermissions?.reporting.reports ?? false,
                              active: routeID === RouteID.REPORTING_PORTAL || currentJSPPage === Page.REPORTING,
                              favourite: favourites?.includes(SidebarMenuItems.REPORTING),
                              handleClick: (isMiddleClick?: boolean): void => {
                                  if (currentJSPPage === Page.REPORTING) {
                                      setIsReportingMenuOpen(!isReportingMenuOpen)
                                      return
                                  }

                                  redirectToServletWithBranding(
                                      ServletCmds.RPT_PORTAL,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                              }
                          },
                          {
                              id: SidebarMenuItems.INSIGHTS,
                              icon: IconName.INSIGHTS_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Reporting_Insights),
                              enabled: userPagePermissions?.reporting.insights ?? false,
                              active: routeID === RouteID.INSIGHTS || currentJSPPage === Page.INSIGHTS_HUB,
                              favourite: favourites?.includes(SidebarMenuItems.INSIGHTS),
                              handleClick: (isMiddleClick?: boolean): void => {
                                  if (isNewInsightsEnabled) {
                                      moveToURL(INSIGHTS_ROUTE_PATH)
                                  } else {
                                      redirectToServletWithBranding(
                                          ServletCmds.INSIGHTS_HUB,
                                          brandingURLMapping,
                                          undefined,
                                          undefined,
                                          isMiddleClick
                                      )
                                  }
                              }
                          },
                          {
                              id: SidebarMenuItems.PRESENCE,
                              icon: IconName.PRESENCE_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Reporting_Presence),
                              enabled: userPagePermissions?.reporting.presence ?? false,
                              active: routeID === RouteID.PRESENCE || currentJSPPage === Page.PRESENCE,
                              favourite: favourites?.includes(SidebarMenuItems.PRESENCE),
                              handleClick: (isMiddleClick?: boolean): void => moveToURL(PRESENCE_URL, isMiddleClick)
                          }
                      ],
                      [MainMenuApp.FAST_PROVISIONING]: [
                          {
                              id: SidebarMenuItems.USER_MANAGEMENT,
                              icon: IconName.USER_MANAGEMENT_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_FastProvisioning_UserManagement),
                              enabled: userPagePermissions?.fastProvisioning.userManagement ?? false,
                              favourite: favourites?.includes(SidebarMenuItems.USER_MANAGEMENT),
                              handleClick: (): void => {
                                  UsersByExtensionAdapter.newInstance()
                              }
                          },
                          {
                              id: SidebarMenuItems.QUEUE_MANAGEMENT,
                              icon: IconName.QUEUE_MANAGEMENT_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_FastProvisioning_QueueManagement),
                              enabled: userPagePermissions?.fastProvisioning.queueManagement ?? false,
                              favourite: favourites?.includes(SidebarMenuItems.QUEUE_MANAGEMENT),
                              handleClick: (): void => {
                                  ManageQueuesAdapter.newInstance()
                              }
                          },
                          {
                              id: SidebarMenuItems.SELF_PROVISIONING,
                              icon: IconName.ENTERPRISE_MANAGEMENT_SIDEBAR,
                              title: language.get(
                                  SidebarMenuLanguage.SidebarMenu_FastProvisioning_EnterpriseManagement
                              ),
                              enabled: userPagePermissions?.fastProvisioning.selfProvisioning ?? false,
                              favourite: favourites?.includes(SidebarMenuItems.SELF_PROVISIONING),
                              handleClick: (): void => {
                                  SelfProvisioningAdapter.newInstance()
                              }
                          }
                      ],
                      [MainMenuApp.ADMIN]: [
                          {
                              id: SidebarMenuItems.CME,
                              icon: IconName.CME_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_CME),
                              enabled: hasPermission(userInfo, Permissions.CME_READ),
                              active: currentJSPPage === Page.ADMIN_CMENVIRONMENTS,
                              favourite: favourites?.includes(SidebarMenuItems.CME),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_CMENVIRONMENTS,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.TEL_SERVERS,
                              icon: IconName.TELSERVERS_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_TelephonyServers),
                              enabled: hasPermission(userInfo, Permissions.TEL_SYS_READ),
                              active: currentJSPPage === Page.ADMIN_TELSYSS,
                              favourite: favourites?.includes(SidebarMenuItems.TEL_SERVERS),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_TELSYSS,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.PARTITIONS,
                              icon: IconName.PARTITIONS_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_Partitions),
                              enabled: hasPermission(userInfo, Permissions.PARTITION_READ),
                              active: currentJSPPage === Page.ADMIN_PARTITIONS,
                              favourite: favourites?.includes(SidebarMenuItems.PARTITIONS),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_PARTITIONS,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.USERS,
                              icon: IconName.USERS_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_AppUsers),
                              enabled: hasPermission(userInfo, Permissions.USER_READ),
                              active: currentJSPPage === Page.ADMIN_USERS,
                              favourite: favourites?.includes(SidebarMenuItems.USERS),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_USERS,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.DEVICES,
                              icon: IconName.DEVICES_SIDERBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_Devices),
                              enabled: hasPermission(userInfo, Permissions.DEVICE_READ),
                              active: currentJSPPage === Page.ADMIN_DEVICES,
                              favourite: favourites?.includes(SidebarMenuItems.DEVICES),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_DEVICES,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.AGENTS,
                              icon: IconName.AGENTS_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_Agents),
                              enabled: hasPermission(userInfo, Permissions.ACDAGENT_READ),
                              active: currentJSPPage === Page.ADMIN_AGENTS,
                              favourite: favourites?.includes(SidebarMenuItems.AGENTS),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_AGENTS,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.DIRECTORY,
                              icon: IconName.DIRECTORY_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_Directory),
                              enabled: hasPermission(userInfo, Permissions.DIRECTORY_ENTRY_READ),
                              active: currentJSPPage === Page.ADMIN_DIRECTORY,
                              favourite: favourites?.includes(SidebarMenuItems.DIRECTORY),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_DIRECTORY,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.CODES,
                              icon: IconName.CODES_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_Codes),
                              enabled: hasPermission(userInfo, Permissions.CODE_READ),
                              active: currentJSPPage === Page.ADMIN_CODES,
                              favourite: favourites?.includes(SidebarMenuItems.CODES),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_CODES,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.CRM_INTEGRATION,
                              icon: IconName.CRM_CONFIGURATION_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_CRM_Configuration),
                              enabled:
                                  !!crmConfigEnabled &&
                                  hasPermission(userInfo, Permissions.CRM_CONFIG_READ) &&
                                  isCXIEnabled,
                              active: routeID === RouteID.CRM_INTEGRATION,
                              favourite: favourites?.includes(SidebarMenuItems.CRM_INTEGRATION),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  moveToURL(CRM_INTEGRATION_ROUTE_PATH, isMiddleClick)
                          },
                          {
                              id: SidebarMenuItems.CLIENT_CONFIG,
                              icon: IconName.CLIENT_CONFIGURATION_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_ClientConfig),
                              enabled:
                                  !!clientConfigEnabled &&
                                  hasPermission(userInfo, Permissions.CLIENT_CONFIG_READ) &&
                                  isCXIEnabled,
                              active: routeID === RouteID.CLIENT_CONFIGURATION,
                              favourite: favourites?.includes(SidebarMenuItems.CLIENT_CONFIG),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  moveToURL(CLIENT_CONFIGURATION_ROUTE_PATH, isMiddleClick)
                          },
                          {
                              id: SidebarMenuItems.RECORDINGS,
                              icon: IconName.RECORDINGS_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_Recording),
                              enabled: hasPermission(userInfo, Permissions.RECORDING_READ),
                              active: currentJSPPage === Page.ADMIN_CALLRECINTS,
                              favourite: favourites?.includes(SidebarMenuItems.RECORDINGS),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_CALLRECINTS,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.MESSAGE,
                              icon: IconName.MESSAGE_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_Messages),
                              enabled: hasPermission(userInfo, Permissions.MESSAGE_READ),
                              active: currentJSPPage === Page.ADMIN_MESSAGES,
                              favourite: favourites?.includes(SidebarMenuItems.MESSAGE),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_MESSAGES,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.DATABASE,
                              icon: IconName.DATABASE_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_Admin_Database),
                              enabled: hasPermission(userInfo, Permissions.DATABASE_READ),
                              active: currentJSPPage === Page.ADMIN_DATABASE,
                              favourite: favourites?.includes(SidebarMenuItems.DATABASE),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_DATABASE,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          }
                      ],
                      [MainMenuApp.STATUS]: [
                          {
                              id: SidebarMenuItems.LICENCES,
                              icon: IconName.LICENCES_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_StatusTools_Licences),
                              enabled: hasPermission(userInfo, Permissions.LICENSE_READ),
                              active: currentJSPPage === Page.LICENSEMGR,
                              favourite: favourites?.includes(SidebarMenuItems.LICENCES),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.LICENSEMGR,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.LOGGING,
                              icon: IconName.LOGGING_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_StatusTools_Logging),
                              enabled: hasPermission(userInfo, Permissions.LOGGING_READ),
                              active: currentJSPPage === Page.ADMIN_LOGGING,
                              favourite: favourites?.includes(SidebarMenuItems.LOGGING),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.ADMIN_LOGGING,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.LOG_MANAGEMENT,
                              icon: IconName.LOG_MANAGEMENT_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_StatusTools_LogManagement),
                              enabled: hasPermission(userInfo, Permissions.LOG_MANAGEMENT_READ),
                              active: currentJSPPage === Page.LOGFILEMGR,
                              favourite: favourites?.includes(SidebarMenuItems.LOG_MANAGEMENT),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.LOGFILEMGR,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.AUDIT_LOGS,
                              icon: IconName.AUDIT_LOGS_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_StatusTools_AuditTrail),
                              enabled: hasPermission(userInfo, Permissions.AUDIT_TRAIL),
                              active: currentJSPPage === Page.AUDITTRAIL,
                              favourite: favourites?.includes(SidebarMenuItems.AUDIT_LOGS),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.AUDITTRAIL,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.TELEPHONY_MODULES_STATUS,
                              icon: IconName.TELSYS_MODULE_STATUS_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_StatusTools_TelephoneModuleStatus),
                              enabled: hasPermission(userInfo, Permissions.TELSYS_STATUS),
                              active: currentJSPPage === Page.TELMODULE_STATUS,
                              favourite: favourites?.includes(SidebarMenuItems.TELEPHONY_MODULES_STATUS),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.TELMODULE_STATUS,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.CALL_CONTROL,
                              icon: IconName.CALL_CONTROL_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_StatusTools_CallControl),
                              enabled: hasPermission(userInfo, Permissions.CALL_CONTROL_READ),
                              active: currentJSPPage === Page.TELMODULE_CALLCTRL,
                              favourite: favourites?.includes(SidebarMenuItems.CALL_CONTROL),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.TELMODULE_CALLCTRL,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          },
                          {
                              id: SidebarMenuItems.SERVER_STATUS,
                              icon: IconName.SERVER_STATUS_SIDEBAR,
                              title: language.get(SidebarMenuLanguage.SidebarMenu_StatusTools_ServerStatus),
                              enabled: hasPermission(userInfo, Permissions.SERVER_STATUS),
                              active: currentJSPPage === Page.APP_STATUS,
                              favourite: favourites?.includes(SidebarMenuItems.SERVER_STATUS),
                              handleClick: (isMiddleClick?: boolean): void =>
                                  redirectToServletWithBranding(
                                      ServletCmds.APP_STATUS,
                                      brandingURLMapping,
                                      undefined,
                                      undefined,
                                      isMiddleClick
                                  )
                          }
                      ]
                  },
        [
            brandingURLMapping,
            currentJSPPage,
            routeID,
            userInfo,
            userPagePermissions,
            isReportingMenuOpen,
            favourites,
            isCXIEnabled
        ]
    )

    const currentSubMenu: Nullable<SubMenuItem[]> = useMemo(
        (): Nullable<SubMenuItem[]> => (mainMenuApp?.app ? subMainMenuItems[mainMenuApp.app] : []),
        [mainMenuApp, subMainMenuItems]
    )

    const handleOnClickFavourite: (id: SidebarMenuItems) => void = useCallback(
        (id: SidebarMenuItems): void => {
            const filteredFavourites: SidebarMenuItems[] = favourites?.includes(id)
                ? favourites.filter((fav: SidebarMenuItems): boolean => fav !== id)
                : [...(favourites ?? []), id]

            setFavourites(filteredFavourites)
            updateUserFavourites(filteredFavourites).catch(displayError)
        },
        [favourites]
    )

    useEffect((): void => {
        if (isVarNotEmpty(userSettings) && isMounted.current) {
            setFavourites(userSettings?.Favourites ?? [])
        }
    }, [userSettings])

    return (
        <div className='sidebar-menu-container'>
            <div className='sidebar-menu'>
                {currentSubMenu ? (
                    currentSubMenu.map(
                        (sidebarMenuItem: SubMenuItem): ReactElement => (
                            <SideMenuEntry
                                key={sidebarMenuItem.id}
                                sidebarMenuItem={sidebarMenuItem}
                                isSidebarExpanded={isSidebarExpanded}
                                favourites={favourites}
                                handleOnClickFavourite={handleOnClickFavourite}
                                disableFavourites={!mainMenuApp?.showAppMenu}
                            />
                        )
                    )
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
